import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import Select from '../../../../../Select';
import {
  CARD_SIDE,
  ORIENTATION,
} from '../../../../../../../services/cards/constants';
import SecondaryControlButton from '../../../../../../../services/prints/components/PrintProductConfigurator/components/PrintProductConfiguratorControlsPane/SecondaryControlButton';
import SecondaryControlButtonSizeRectangle from '../../../../../../../services/prints/components/PrintProductConfigurator/components/PrintProductConfiguratorControlsPane/SecondaryControlButton/SecondaryControlButtonSizeRectangle';
import getPrintPartners from '../../../../../../../services/cards/graphql/queries/getPrintPartners';
import { getCardTemplateSizing } from '../../../../../../../services/cards/components/AccountCard/helpers';
import { getCardSide } from '../../../../../CardDesigner/helpers';
import getPrintProducts from '../../../../../../../services/cards/graphql/queries/getPrintProducts';
import { isStandardPrint } from '../../../../../../../util/print-product';
import { DESIGN_MODES } from '../../../../../CardDesigner/constants';

const propTypes = {
  className: PropTypes.string,
  onCardSettingsUpdate: PropTypes.func.isRequired,
  card: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  designMode: PropTypes.oneOf(Object.values(DESIGN_MODES)),
};

const CollageDesignerLayoutControls = ({
  className,
  onCardSettingsUpdate,
  card,
  designMode,
}) => {
  const classes = classNames({
    [className]: !!className,
  });

  const { data: { printPartners } = {} } = useQuery(getPrintPartners, {
    variables: {
      // Hard-coded to "Bay Photo" for now
      // This is collages, which is always Bay Photo as it relies on Waldo
      name: 'Bay Photo',
    },
  });

  const [printPartner] = printPartners?.records ?? [];

  const { data: { printProducts: printProductsResult } = {} } = useQuery(
    getPrintProducts,
    {
      variables: {
        print_partner_id: printPartner?.id,
      },
      skip: !printPartner?.id,
    },
  );

  const printProducts = printProductsResult?.records ?? [];
  const standardPrintProduct = printProducts.find(printProduct =>
    isStandardPrint(printProduct),
  );
  const printProductSizes = standardPrintProduct?.sizes?.records ?? [];
  const cardSide = getCardSide({ card, side: CARD_SIDE.FRONT });
  const cardTemplate = cardSide?.card_template;
  const orientation = cardTemplate?.orientation ?? ORIENTATION.LANDSCAPE;

  return (
    <div className={classes}>
      <Select
        id="card_designer_orientation"
        name="card_designer_orientation"
        className="mb-2"
        value={orientation}
        size="sm"
        onChange={async event => {
          const { value } = event.target;
          await onCardSettingsUpdate({
            cardTemplateId: cardTemplate?.id,
            values: {
              orientation: value,
            },
            designMode,
          });
        }}
        label="Orientation"
      >
        <option value={ORIENTATION.LANDSCAPE}>Landscape</option>
        <option value={ORIENTATION.PORTRAIT}>Portrait</option>
      </Select>

      <div className="card-designer-layout-controls-list">
        {printProductSizes.map(printProductSize => {
          const printSize = printProductSize.print_size;
          const { width, height } = getCardTemplateSizing({
            orientation,
            print_size: {
              width: printSize.width,
              height: printSize.height,
            },
          });

          return (
            <SecondaryControlButton
              key={printProductSize.id}
              title={printProductSize.print_size.name}
              icon={
                <SecondaryControlButtonSizeRectangle
                  width={width}
                  height={height}
                  orientation={orientation}
                />
              }
              onClick={async () => {
                await onCardSettingsUpdate({
                  cardTemplateId: cardTemplate?.id,
                  values: {
                    print_size_id: printProductSize.print_size.id,
                    orientation,
                  },
                });
              }}
              active={
                cardTemplate?.print_size?.id === printProductSize.print_size.id
              }
            />
          );
        })}
      </div>
    </div>
  );
};

CollageDesignerLayoutControls.propTypes = propTypes;

export default CollageDesignerLayoutControls;
